import React, { useMemo } from 'react';
import { AccommodationsPushStepProps } from './AccommodationsPushStep.types';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { AccommodationOfferingCard } from './AccommodationOfferingCard';
// import { useCurrencyCodeFromBrowserLocale } from '@shared/utils/currency/useCurrencyCodeFromBrowseLocale';
// import { getProposedBookingDates } from './AccommodationsPushStep.utils';
import { useThanksForRSVPTelemetry } from '@apps/thanksForRsvp/ThanksForRSVP.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Content } from './Content';
import { useTranslation } from '@shared/core';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { AccommodationPlaceTypeExtended, EventAccommodationData, ExtendedEventAccommodationPublicData } from '@shared/hooks/useEventAccommodationsCheck';
import { SearchParams, useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';

// const RateSection = (props: { accommodationPlaceId: string; eventFinalizedDate: NonNullable<AccommodationsPushStepProps['eventFinalizedDate']> }) => {
//   const currencyCode = useCurrencyCodeFromBrowserLocale();

//   const bookingDates = useMemo(() => getProposedBookingDates(props.eventFinalizedDate, { checkinDelta: 0 }), [props.eventFinalizedDate]);

//   const validatedCurrencyCode = currencyCode in TravelCurrencyCode ? TravelCurrencyCode[currencyCode as keyof typeof TravelCurrencyCode] : TravelCurrencyCode.USD;

//   const { data, loading } = useEventAccommodationOffersQuery({
//     variables: {
//       payload: {
//         currencyCode: validatedCurrencyCode,
//         occupancies: { occupancies: [{ adults: 2, childrenAges: [] }], unitCount: 1 },
//         dates: {
//           checkIn: { low: bookingDates.checkin, high: bookingDates.checkin },
//           checkOut: { low: bookingDates.checkout, high: bookingDates.checkout }
//         },
//         propertyIdentifiers: [{ accommodationPlaceId: props.accommodationPlaceId }]
//       }
//     },
//     batchMode: 'fast'
//   });

//   const cheapestDailyRateOffer = useMemo(() => {
//     return data?.travelOffersForAccommodationProperties?.someOffers?.[0]?.totalAmount;
//   }, [data]);

//   return !loading && typeof cheapestDailyRateOffer === 'undefined' ? null : (
//     <AccommodationOfferingCard.RateSection
//       rateType="daily"
//       marginTop={5}
//       rateInMinorUnits={cheapestDailyRateOffer?.valueInMinorUnits || 0}
//       currency={cheapestDailyRateOffer?.currency}
//       showSkeleton={loading}
//     />
//   );
// };

const AccommodationListing = ({
  data,
  eventFinalizedDate,
  eventHandle,
  hotelUrl,
  onSelectListingClick,
}: {
  eventHandle: string;
  data: ExtendedEventAccommodationPublicData;
  eventFinalizedDate: Maybe<{ milliseconds: number; timezone: string }>;
  hotelUrl: string;
  onSelectListingClick: () => void;
}) => {
  const accom = useMemo(() => {
    return {
      id: data.id,
      type: data.type,
      displayName: data.customName || data.name,
      photoUrl: data.customPhoto?.url || data.photo?.url || DEFAULT_HOTEL_IMAGE,
      distanceFromVenue: data.distanceFromVenue,
      isFromAccommodationCatalog: data.isFromAccommodationCatalog,
    };
  }, [data]);

  return (
    <AccommodationOfferingCard marginTop={7}>
      {accom.photoUrl && (
        <AccommodationOfferingCard.AssetContainer>
          <AccommodationOfferingCard.Asset imageUrl={accom.photoUrl} />
        </AccommodationOfferingCard.AssetContainer>
      )}

      {accom.displayName && (
        <AccommodationOfferingCard.TitleSection
          marginTop={5}
          title={accom.displayName}
          distanceFromVenue={accom.isFromAccommodationCatalog ? accom.distanceFromVenue : undefined}
          isPreferredOffering={accom.type === AccommodationPlaceTypeExtended.hotel && accom.isFromAccommodationCatalog}
        />
      )}

      {/* {accom.isFromAccommodationCatalog && eventFinalizedDate && <RateSection accommodationPlaceId={accom.id} eventFinalizedDate={eventFinalizedDate} />} */}

      <ButtonV2
        // Must perform a full page redirect instead of in-app nav
        as="a"
        href={hotelUrl}
        variant="solid"
        intent="neutral"
        fullWidth
        shape="rounded"
        marginTop={7}
        onClick={() => {
          onSelectListingClick();
        }}
      >
        Select Hotel
      </ButtonV2>
    </AccommodationOfferingCard>
  );
};

const RoomBlockListing = (props: {
  eventHandle: string;
  data: ExtendedEventAccommodationPublicData;
  eventFinalizedDate: Maybe<{ milliseconds: number; timezone: string }>;
  onSelectListingClick: () => void;
}) => {
  const { data, eventHandle, onSelectListingClick } = props;
  const hotelRoutePaths = useHotelRoutePaths();

  const roomBlockData = data.accommodationRoomBlock;
  const accom = useMemo(() => {
    return {
      id: data.id,
      type: data.type,
      displayName: data.customName || data.name,
      photoUrl: data.photo?.url || DEFAULT_HOTEL_IMAGE,
      distanceFromVenue: data.distanceFromVenue,
      note: data.userNote,
      pricePerNightString: roomBlockData?.pricePerNight ? `$${roomBlockData?.pricePerNight.toString()}` : undefined,
      bookingUrl: roomBlockData?.bookingUrl,
      cutoffDate: roomBlockData?.cutoffDate
        ? createDateFromUnformattedString(roomBlockData?.cutoffDate)?.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
        : null,
      strikeoutPricePerNightString: roomBlockData?.strikeoutPricePerNight ? `$${roomBlockData?.strikeoutPricePerNight?.toString()}` : undefined,
      interestedGuestCount: null,
    };
  }, [data, roomBlockData]);

  const searchParams: SearchParams = {
    utm_medium: 'web',
    utm_source: 'thanks_for_rsvp',
    utm_campaign: 'has_own_accommodations',
  };

  return (
    <AccommodationOfferingCard marginTop={7}>
      {accom.photoUrl && (
        <AccommodationOfferingCard.AssetContainer>
          <AccommodationOfferingCard.Asset imageUrl={accom.photoUrl} />
        </AccommodationOfferingCard.AssetContainer>
      )}

      {accom.displayName && (
        <AccommodationOfferingCard.TitleSection
          marginTop={5}
          title={accom.displayName}
          distanceFromVenue={accom.distanceFromVenue}
          isPreferredOffering={true}
        />
      )}

      <AccommodationOfferingCard.Divider />

      <AccommodationOfferingCard.RoomBlockRateSection
        cutoffDate={accom.cutoffDate}
        strikeoutPricePerNightString={accom.strikeoutPricePerNightString}
        pricePerNightString={accom.pricePerNightString}
      />

      {accom.note && <AccommodationOfferingCard.Divider />}

      {accom.note && <AccommodationOfferingCard.Note note={accom.note} />}

      {accom.bookingUrl && (
        <ButtonV2
          // Must perform a full page redirect instead of in-app nav
          as="a"
          href={hotelRoutePaths.roomblock.buildRelativeUrl(eventHandle, accom.id, searchParams)}
          variant="solid"
          intent="neutral"
          fullWidth
          shape="rounded"
          marginTop={7}
          onClick={() => {
            onSelectListingClick();
          }}
        >
          Select Room
        </ButtonV2>
      )}
    </AccommodationOfferingCard>
  );
};

interface HasOwnAccommodationsCohortViewProps extends AccommodationsPushStepProps {}
export const HasOwnAccommodationsCohortView = (props: HasOwnAccommodationsCohortViewProps) => {
  const { accommodationListings, eventDisplayName } = props;

  const { t } = useTranslation('thanksForRSVP');

  const paths = useHotelRoutePaths();

  const transAccommodationsStep = useMemo(() => {
    return t('accommodationsPushStep');
  }, [t]);

  const { accommodationPushStepButtonClick } = useThanksForRSVPTelemetry();

  const handleOnBrowseHotelsClick = useEventCallback((accom: EventAccommodationData, listingPosition: number) => {
    accommodationPushStepButtonClick({
      cta: 'viewHotelPdp',
      cohort: 'hasOwnAccommodations',
      targetAccommodationListingPosition: listingPosition,
      totalAccommodationCount: accommodationListings.length,
      accommodationType: accom.type,
    });
  });
  return (
    <Content
      title={
        eventDisplayName
          ? transAccommodationsStep.hasOwnAccommodationsCohort.titleWithDisplayName({ eventDisplayName })
          : transAccommodationsStep.hasOwnAccommodationsCohort.genericTitle()
      }
      message={transAccommodationsStep.hasOwnAccommodationsCohort.message()}
    >
      <Flex flexDirection="column">
        {accommodationListings.map((accom, idx) => {
          if (accom.type === 'roomBlock') {
            return (
              <RoomBlockListing
                key={accom.id}
                {...props}
                data={accom}
                onSelectListingClick={() => handleOnBrowseHotelsClick(accom, idx)}
              />
            );
          }
          const hotelUrl = paths.accommodationPlace.buildRelativeUrl(props.eventHandle, accom.id, {
            utm_medium: 'web',
            utm_source: 'thanks_for_rsvp',
            utm_campaign: 'has_own_accommodations',
          });
          return (
            <AccommodationListing
              key={accom.id}
              {...props}
              data={accom}
              hotelUrl={hotelUrl}
              onSelectListingClick={() => handleOnBrowseHotelsClick(accom, idx)}
            />
          );
        })}
      </Flex>
    </Content>
  );
};
